import { Ban, CheckCircle2, ClipboardCheck, Clock, Download, FileText, Mail, Send, XCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth, usePermission } from '../../../../auth/AuthContext';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { ErrorState } from '../../../../components/ErrorState';
import { FolderTabs } from '../../../../components/FolderTabs';
import { NextActionAlert } from '../../../../components/NextActionAlert';
import { LifecycleTimeline } from '../../../../components/LifecycleTimeline';
import type { MenuItem } from '../../../../components/Menu';
import { PreviewGrid } from '../../../../components/PreviewGrid';
import { PreviewTable } from '../../../../components/PreviewTable';
import { RichTextDocument } from '../../../../components/RichTextDocument';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner, type ProfileBannerTone } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText, type StatusTone } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly, formatDateTime, toIso } from '../../../../lib/formatDate';
import { saveBlob } from '../../../../lib/saveBlob';
import type { TrainingReportAttachment } from '../../../self-service/surveys/mySurveyTypes';
import { personDisplayName, usePersonNames } from '../../../human-resources/personnel/usePersonNames';
import { COMPETENCY_LEVEL_LABELS } from '../../../human-resources/personnel/personnelTypes';
import {
  SurveyDevelopmentPlanModal,
  type SurveyDevelopmentPlanStep,
} from '../../survey/components/SurveyDevelopmentPlanModal';
import { ResponseAnswers } from '../../survey/components/SurveyResponseViewModal';
import { surveyRespondentsApi } from '../../survey/surveyApi';
import { SURVEY_PLAN_STATUS, type SurveyDevelopmentNeed, type SurveyResponseView } from '../../survey/surveyTypes';
import { DevelopmentPlanReviewModal, type DevelopmentPlanReviewStage } from '../components/DevelopmentPlanReviewModal';
import { ImpactAssessmentModal } from '../components/ImpactAssessmentModal';
import { developmentPlansApi } from '../developmentPlanApi';
import { planStatusLabel, type DevelopmentPlan, type DevelopmentPlanDetail } from '../developmentPlanTypes';
import { developmentPlanLifecycle } from '../utils/developmentPlanLifecycle';

type Tab = 'info' | 'preview' | 'cycle';

type PreviewKey = 'survey' | 'plan' | 'report';

/**
 * One development plan — Learning & Development › Development Plan › a plan.
 *
 * On the **header** layout with three tabs: **Info** — the response it came from and the
 * assessment; **Preview** — a second strip of folder tabs, as on the employee's own page: Survey
 * (the answers the plan was written from, on `surveys.read`), Development Plan (the needs and the
 * plan) and Report (the training report, its attachments and the impact assessment), none of
 * which appears anywhere else on the page; and **Life cycle**, the steps the plan has
 * been through: assessed, written, submitted, approved or rejected. The header's pill is where the
 * plan stands (Deleted wins).
 *
 * **Every card is Survey's record**, written under Survey's own keys. Preview's two cards carry
 * Edit in their cog, opening Survey's plan form on `surveys.plan`; Info's cards do not, since the
 * survey and the assessment are not changed from here. SUBMIT is the record-level lifecycle action
 * the banner owns. Each card's cog also links out to the survey for a reader who administers it.
 */
export function DevelopmentPlanProfilePage() {
  // Reached from a plan's Personnel tab — `planId` is the plan, `id` the employee's own response.
  const { planId, id } = useParams<{ planId: string; id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canReadPeople = usePermission('personnel.read');

  const [detail, setDetail] = useState<DevelopmentPlanDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  // Which step the plan form opens on; null while it is closed.
  const [planning, setPlanning] = useState<SurveyDevelopmentPlanStep | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sending, setSending] = useState(false);
  const [running, setRunning] = useState(false);
  const [submitError, setSubmitError] = useState<ApiError | null>(null);
  const [tab, setTab] = useState<Tab>('info');
  const [preview, setPreview] = useState<PreviewKey>('survey');
  // Survey's answers need Survey's read; without it the strip hides that tab, and the panel follows it.
  const previewView: PreviewKey = preview === 'survey' && !hasPermission('surveys.read') ? 'plan' : preview;
  // Which decision's form is open; null while none is. The page already shows the preview.
  const [deciding, setDeciding] = useState<DevelopmentPlanReviewStage | null>(null);
  // Create impact assessment — or edit it.
  const [assessingImpact, setAssessingImpact] = useState(false);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      developmentPlansApi
        .get(id, signal)
        .then((next) => {
          if (!signal?.aborted) {
            setDetail(next);
            setError(null);
          }
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this development plan.', 0));
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  // A different record drops what is on screen — DERIVED, so the old plan never shows under the new id.
  const plan = detail != null && detail.summary.id === id ? detail.summary : null;

  // `!= null`: a withheld id arrives as undefined, not null.
  const personIds = useMemo(
    () => (canReadPeople && plan?.personId != null ? [plan.personId] : []),
    [canReadPeople, plan],
  );

  const names = usePersonNames(personIds);
  const name = plan ? (!canReadPeople && plan.personId != null ? 'Hidden' : personDisplayName(plan, names)) : null;

  // The way back is the PLAN this copy was created from — its Personnel tab is where the reader came from.
  const planCrumbId = plan?.planId ?? planId;
  const breadcrumb: BreadcrumbSegment[] = [
    'Learning & Development',
    { label: 'Development Plan', to: '/learning-development/development-plan' },
    ...(planCrumbId != null
      ? [{ label: plan?.planTitle ?? 'Plan', to: `/learning-development/development-plan/${planCrumbId}` }]
      : []),
    name ?? 'Employee',
  ];

  async function downloadReportFile(attachment: TrainingReportAttachment) {
    if (!plan) return;

    try {
      const { blob, fileName } = await developmentPlansApi.downloadTrainingReportAttachment(plan.id, attachment.id);
      saveBlob(blob, fileName ?? attachment.fileName);
    } catch (caught) {
      toast.failure(caught instanceof ApiError ? caught.message : 'Could not download the attached file.');
    }
  }

  async function runSend() {
    if (!plan) return;

    setRunning(true);
    setSubmitError(null);

    try {
      await surveyRespondentsApi.sendDevelopmentPlan(plan.surveyId, plan.id);
      toast.success('Sent the development plan to the employee.');
      setSending(false);
      load();
    } catch (caught) {
      setSubmitError(caught instanceof ApiError ? caught : new ApiError('The plan could not be sent.', 0));
    } finally {
      setRunning(false);
    }
  }

  async function runSubmit() {
    if (!plan) return;

    setRunning(true);
    setSubmitError(null);

    try {
      await surveyRespondentsApi.submitDevelopmentPlan(plan.surveyId, plan.id);
      toast.success('Submitted the development plan for approval.');
      setSubmitting(false);
      load();
    } catch (caught) {
      setSubmitError(caught instanceof ApiError ? caught : new ApiError('The plan could not be submitted.', 0));
    } finally {
      setRunning(false);
    }
  }

  // A failed REFRESH keeps the page; only a first load that failed replaces it.
  if (error && !plan) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (!plan || !detail) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const written = plan.developmentPlanAt != null;
  const changeable =
    plan.isActive &&
    plan.developmentPlanStatus !== SURVEY_PLAN_STATUS.Submitted &&
    plan.developmentPlanStatus !== SURVEY_PLAN_STATUS.Approved &&
    plan.developmentPlanStatus !== SURVEY_PLAN_STATUS.Accepted;
  /*
    THE APPROVAL, in the banner — offered only to the approver the request is WAITING ON: the
    engine's own answer for this reader, plus the engine's key. Everyone else reads the sentence.
  */
  const canDecide =
    plan.isActive &&
    plan.developmentPlanStatus === SURVEY_PLAN_STATUS.Submitted &&
    detail.approval?.canDecide === true &&
    hasPermission('approvals.decide');
  // APPROVED IS NOT SENT: an approved plan whose form was not ticked waits here to be sent.
  const awaitingSend =
    plan.isActive && plan.developmentPlanStatus === SURVEY_PLAN_STATUS.Approved && plan.sentAt == null;
  const state = bannerFor(plan, canDecide);
  const reportAttachments = detail.trainingReportAttachments ?? [];
  // An impact is assessed against a SUBMITTED report, on a live response — the server refuses anything else.
  const canAssessImpact = plan.isActive && plan.trainingReportAt != null;
  const planStatus = planStatusLabel(plan);

  /*
    Preview's Edit — the cog's first entry on both of its cards. It opens Survey's own plan form,
    which saves the needs and the plan TOGETHER, so the two cards share one Edit rather than each
    owning a dedicated one. Hidden without `surveys.plan`; and since the form loads the response
    through Survey's read, without that key too. Disabled, with the reason, for STATE.
  */
  const canOpenForm = hasPermission('surveys.read');
  // Each card's Edit opens the form ON ITS OWN STEP — the needs on the needs, the plan on the plan.
  const onEditNeeds = canOpenForm ? () => setPlanning('needs') : undefined;
  const onEditPlan = canOpenForm ? () => setPlanning('plan') : undefined;
  const editDisabledReason = !plan.isActive
    ? 'This response is deleted, so its plan can no longer be changed.'
    : plan.developmentPlanStatus === SURVEY_PLAN_STATUS.Submitted
      ? 'This development plan is waiting for approval, so it cannot be changed.'
      : plan.developmentPlanStatus === SURVEY_PLAN_STATUS.Approved
        ? 'This development plan has been approved, so it cannot be changed.'
        : plan.developmentPlanStatus === SURVEY_PLAN_STATUS.Accepted
          ? 'The employee accepted this development plan, so it is final.'
          : null;

  // Survey's record: the way to it, for a reader who has the key.
  const openSurvey: MenuItem[] = [
    {
      key: 'open-survey',
      label: 'Open survey',
      permission: 'surveys.read',
      onClick: () => navigate(`/learning-development/survey/${plan.surveyId}`),
    },
  ];

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="development-plan-profile"
        header={
          <ProfileHeader
            id="development-plan-profile-header"
            icon={<FileText className="h-6 w-6" aria-hidden="true" />}
            title={name ?? plan.employeeNumber}
            meta={[plan.surveyTitle]}
            status={
              plan.isActive ? (
                // Before a plan is written there is no status to show — the response's own Active stands in.
                planStatus != null ? (
                  <StatusText tone={toneFor(plan)}>{planStatus}</StatusText>
                ) : (
                  <StatusText tone="active">Active</StatusText>
                )
              ) : (
                <StatusText tone="inactive">Deleted</StatusText>
              )
            }
            createdBy={plan.createdByName ?? null}
            createdAt={plan.createdAt}
          />
        }
        banner={
          <ProfileBanner
            id="development-plan-profile-banner"
            tone={state.tone}
            icon={state.icon}
            message={state.message}
            /*
              The ACTION is gated; the sentence is for every reader. Write only OPENS the form,
              whose own tick confirms; Submit commits, so it confirms. The form loads the response
              through Survey's read, so Write needs that key as well as `surveys.plan`.
            */
            action={
              canDecide ? (
                // Each only OPENS the decision's form, whose remarks and tick are the confirmation.
                <div className="flex items-center gap-2">
                  <Button id="development-plan-decline" variant="secondary" onClick={() => setDeciding('decline')}>
                    Decline
                  </Button>
                  <Button id="development-plan-approve" variant="primary" onClick={() => setDeciding('approve')}>
                    Approve
                  </Button>
                </div>
              ) : awaitingSend && !plan.isRestricted ? (
                // It WRITES — the employee can read the plan the moment it lands — so it confirms.
                <PermissionGate permission="surveys.send-plan">
                  <Button
                    id="development-plan-send"
                    variant="primary"
                    leading={<Mail className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => setSending(true)}
                  >
                    Send to employee
                  </Button>
                </PermissionGate>
              ) : !changeable || plan.isRestricted ? undefined : written &&
                // A DECLINED plan reopens exactly as a rejected one does — the employee's refusal.
                plan.developmentPlanStatus !== SURVEY_PLAN_STATUS.Rejected &&
                plan.developmentPlanStatus !== SURVEY_PLAN_STATUS.Declined ? (
                <div className="flex items-center gap-2">
                  {hasPermission('surveys.read') ? (
                    <PermissionGate permission="surveys.plan">
                      <Button id="development-plan-write" variant="secondary" onClick={() => setPlanning('preview')}>
                        Edit plan
                      </Button>
                    </PermissionGate>
                  ) : null}
                  <PermissionGate permission="surveys.submit-plan">
                    <Button
                      id="development-plan-submit"
                      variant="primary"
                      leading={<Send className="h-4 w-4" aria-hidden="true" />}
                      onClick={() => setSubmitting(true)}
                    >
                      Submit for approval
                    </Button>
                  </PermissionGate>
                </div>
              ) : hasPermission('surveys.read') ? (
                <PermissionGate permission="surveys.plan">
                  <Button
                    id="development-plan-write"
                    variant="primary"
                    leading={<FileText className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => setPlanning('preview')}
                  >
                    {written ? 'Revise plan' : 'Write development plan'}
                  </Button>
                </PermissionGate>
              ) : undefined
            }
          />
        }
        tabs={[
          { key: 'info', label: 'Info' },
          // The plan itself, read as one document — the needs identified and the plan written from them.
          { key: 'preview', label: 'Preview' },
          // The page's own read: every step comes from this module's own endpoint.
          { key: 'cycle', label: 'Life cycle' },
        ]}
        activeTab={tab}
        onTabChange={(next) => setTab(next as Tab)}
        // Preview owns its own chrome — a second strip of folder tabs — so it runs edge to edge.
        panel={tab === 'preview' ? 'bleed' : 'cards'}
      >
        {tab === 'cycle' ? (
          // No cog: nothing on a life cycle is edited — the Audit card's exemption.
          <ProfileCard id="development-plan-profile-cycle" title="Life cycle" padded>
            <LifecycleTimeline
              id="development-plan-lifecycle"
              events={developmentPlanLifecycle(detail)}
              // The card already names it.
              title={null}
              empty="Nothing has happened yet."
            />
          </ProfileCard>
        ) : tab === 'preview' ? (
          /*
            A SECOND strip of folder tabs, the employee's own Preview exactly (product call, 12 Oct
            2026): Survey — the answers the plan was written from; Development Plan — the needs and
            the plan; Report — the training report and its impact assessment. The tab is `bleed`, so
            the inset is this wrapper's. The cards keep their cogs: this side EDITS what it shows.
          */
          <div className="flex min-w-0 flex-col p-4">
            <FolderTabs<PreviewKey>
              id="development-plan-preview"
              ariaLabel="Preview"
              paddingX="px-3"
              activeKey={previewView}
              onChange={setPreview}
              tabs={[
                // The ANSWERS are Survey's to read — the tab and its panel share that one gate.
                { key: 'survey', label: 'Survey', permission: 'surveys.read' },
                { key: 'plan', label: 'Development Plan' },
                { key: 'report', label: 'Report' },
              ]}
            />

            {/* All four borders, as Responses' — the active tab's white edge breaks the top. */}
            <div className="rounded-b-panel border border-slate-300 bg-white p-4">
              <div className="mx-auto flex w-full max-w-4xl flex-col gap-4">
                {previewView === 'survey' ? (
                  <SurveyAnswersView
                    surveyId={plan.surveyId}
                    respondentId={plan.id}
                    respondentName={name ?? plan.employeeNumber}
                  />
                ) : previewView === 'plan' ? (
                  <>
                    <ProfileCard
                      id="development-plan-profile-needs"
                      title="Identified Development Needs"
                      count={detail.developmentNeeds.length}
                      onEdit={onEditNeeds}
                      editPermission="surveys.plan"
                      editDisabledReason={editDisabledReason}
                      menuItems={openSurvey}
                      // The grid draws its OWN rounded border, so it sits inset — flush, it was a box jammed
                      // against the card's box with the corners squared off.
                      padded
                      // Nothing identified yet: say so, and offer the way in — the card's own Edit.
                      empty={
                        detail.developmentNeeds.length === 0
                          ? {
                              title: 'No development needs yet',
                              description:
                                'Identify the competencies this employee needs to develop, and the gap in each.',
                              actionLabel: 'Add development needs',
                            }
                          : undefined
                      }
                    >
                      <PreviewGrid<SurveyDevelopmentNeed>
                        rows={detail.developmentNeeds}
                        rowKey={(n) => n.id}
                        empty="No development needs identified yet."
                        columns={[
                          {
                            header: 'Competency',
                            width: 220,
                            render: (n) => <TextCell value={n.competencyName} />,
                          },
                          {
                            header: 'Current level',
                            width: 140,
                            render: (n) => <TextCell value={COMPETENCY_LEVEL_LABELS[n.currentLevel]} />,
                          },
                          {
                            header: 'Target level',
                            width: 140,
                            render: (n) => <TextCell value={COMPETENCY_LEVEL_LABELS[n.targetLevel]} />,
                          },
                          {
                            header: 'Gap identified',
                            width: 260,
                            render: (n) => <TextCell value={n.gap ?? null} />,
                          },
                        ]}
                      />
                    </ProfileCard>

                    <ProfileCard
                      id="development-plan-profile-plan"
                      title="Development Plan"
                      description={
                        written
                          ? `Written by ${plan.developmentPlanByName ?? '—'} on ${formatDateTime(plan.developmentPlanAt!)}`
                          : undefined
                      }
                      onEdit={onEditPlan}
                      editPermission="surveys.plan"
                      editDisabledReason={editDisabledReason}
                      menuItems={openSurvey}
                      padded
                      // No plan written yet: a lone em dash said nothing — say so, and offer the card's own Edit.
                      empty={
                        written
                          ? undefined
                          : {
                              title: 'No development plan yet',
                              description: 'Write the plan for the development needs identified above.',
                              actionLabel: 'Write development plan',
                            }
                      }
                    >
                      {/* The one renderer of stored markup — safe only because the server sanitised it on write. */}
                      <RichTextDocument html={detail.developmentPlan ?? null} />
                    </ProfileCard>
                  </>
                ) : (
                  <>
                    {/*
              THE REPORT the employee submitted on the completed training, with its attachments — the
              third document of the Preview, after the needs and the plan, and shown NOWHERE else on
              the page. Their own record, written from Self Service — so no Edit here. A file is
              DOWNLOADED on its own key: reading that a report carries one is not taking a copy.
            */}
                    <ProfileCard
                      id="development-plan-profile-report"
                      title="Training Report"
                      description={
                        plan.trainingReportAt != null
                          ? `Submitted on ${formatDateTime(plan.trainingReportAt)}`
                          : undefined
                      }
                      padded
                    >
                      {plan.trainingReportAt == null ? (
                        // No Edit to offer — the report is the employee's to submit — so a sentence, not a call to action.
                        <p className="text-sm text-slate-500">
                          {plan.trainingCompletedAt != null
                            ? 'The employee has completed the training and has not submitted a report yet.'
                            : 'No report yet. The employee submits one once their training is completed.'}
                        </p>
                      ) : (
                        <div className="flex flex-col gap-4">
                          {/* The one renderer of stored markup — safe only because the server sanitised it on write. */}
                          <RichTextDocument html={detail.trainingReport ?? null} />

                          {/*
                  THE FILES, one row each. The NAME is this module's read; taking a COPY is its own
                  key — so the button hides without it and the name stays.
                */}
                          <section className="flex flex-col gap-2">
                            <h4 className="text-sm font-medium text-slate-800">Attachments</h4>
                            {reportAttachments.length === 0 ? (
                              <p className="text-sm text-slate-400">—</p>
                            ) : (
                              <ul className="flex flex-col divide-y divide-slate-200 rounded-lg border border-slate-200">
                                {reportAttachments.map((attachment) => (
                                  <li key={attachment.id} className="flex items-center justify-between gap-3 px-3 py-2">
                                    <span className="min-w-0 break-all text-sm text-slate-600">
                                      {attachment.fileName}
                                    </span>
                                    <PermissionGate permission="development-plans.download">
                                      <Button
                                        id={`development-plan-report-download-${attachment.id}`}
                                        variant="secondary"
                                        leading={<Download className="h-4 w-4" aria-hidden="true" />}
                                        onClick={() => void downloadReportFile(attachment)}
                                      >
                                        Download
                                      </Button>
                                    </PermissionGate>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </section>

                          {/*
                    THE REPORT IS IN, and nobody has assessed what the training changed: the card
                    offers the next step — Create impact assessment. The sentence is for every
                    reader; the button hides without `surveys.assess-impact`. Once one exists it has a
                    card of its own below, and this strip is gone.
                  */}
                          {canAssessImpact && plan.impactAssessmentAt == null ? (
                            <NextActionAlert
                              id="development-plan-profile-impact-next"
                              tone="slate"
                              icon={<ClipboardCheck className="h-4 w-4" aria-hidden="true" />}
                              title="No impact assessment yet"
                              description="Record the level the employee reached on each development need after the training, with your remarks."
                              actionLabel="Create impact assessment"
                              onAction={() => setAssessingImpact(true)}
                              actionPermission="surveys.assess-impact"
                            />
                          ) : null}
                        </div>
                      )}
                    </ProfileCard>

                    {/*
              THE IMPACT ASSESSMENT — the level reached on each need beside where it started and
              where it was meant to get to, and the remarks. A CONDITIONAL card: it exists once the
              assessment does. Its own Edit, on the key of the one write behind it.
            */}
                    {plan.impactAssessmentAt != null ? (
                      <ProfileCard
                        id="development-plan-profile-impact"
                        title="Impact Assessment"
                        description={`Assessed by ${plan.impactAssessmentByName ?? '—'} on ${formatDateTime(plan.impactAssessmentAt)}`}
                        onEdit={() => setAssessingImpact(true)}
                        editPermission="surveys.assess-impact"
                        editDisabledReason={
                          canAssessImpact
                            ? null
                            : 'This response is deleted, so its assessment can no longer be changed.'
                        }
                        padded
                      >
                        <div className="flex flex-col gap-4">
                          <PreviewGrid<SurveyDevelopmentNeed>
                            rows={detail.developmentNeeds}
                            rowKey={(n) => n.id}
                            empty="This plan identified no development needs."
                            columns={[
                              {
                                header: 'Competency',
                                width: 220,
                                render: (n) => <TextCell value={n.competencyName} />,
                              },
                              {
                                header: 'Level before',
                                width: 140,
                                render: (n) => <TextCell value={COMPETENCY_LEVEL_LABELS[n.currentLevel]} />,
                              },
                              {
                                header: 'Target level',
                                width: 140,
                                render: (n) => <TextCell value={COMPETENCY_LEVEL_LABELS[n.targetLevel]} />,
                              },
                              {
                                header: 'Level reached',
                                width: 140,
                                render: (n) => (
                                  <TextCell
                                    value={n.achievedLevel != null ? COMPETENCY_LEVEL_LABELS[n.achievedLevel] : null}
                                  />
                                ),
                              },
                            ]}
                          />

                          <section className="flex flex-col gap-1">
                            <h4 className="text-sm font-medium text-slate-800">Remarks</h4>
                            <p className="whitespace-pre-line text-sm text-slate-600">
                              {plan.impactAssessmentRemarks ?? '—'}
                            </p>
                          </section>
                        </div>
                      </ProfileCard>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <>
            <ProfileCard id="development-plan-profile-details" title="Details" menuItems={openSurvey}>
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Survey', value: plan.surveyTitle },
                  { label: 'Questionnaire', value: plan.questionnaireTitle },
                  {
                    label: 'Employee number',
                    value: plan.employeeNumber,
                    mono: true,
                  },
                  { label: 'Division', value: plan.orgUnitName ?? null },
                ]}
              />
            </ProfileCard>

            <ProfileCard id="development-plan-profile-assessment" title="Assessment" menuItems={openSurvey}>
              <PreviewTable
                variant="table-display"
                rows={[
                  {
                    label: 'Date assessed',
                    value: <Instant value={plan.assessedAt} />,
                  },
                  { label: 'Assessed by', value: plan.assessedByName ?? null },
                  { label: 'Remarks', value: plan.assessmentRemarks ?? null },
                ]}
              />
            </ProfileCard>

            {/*
              THE TRAINING the employee started on the accepted plan (12 Oct 2026). Their own record,
              written from Self Service — so no Edit here: the card over another surface's write.
            */}
            {plan.developmentPlanStatus === SURVEY_PLAN_STATUS.Accepted ? (
              <ProfileCard id="development-plan-profile-training" title="Training">
                <PreviewTable
                  variant="table-display"
                  rows={[
                    { label: 'Title', value: plan.trainingTitle ?? null },
                    {
                      label: 'Start date',
                      value: plan.trainingStartDate ? formatDateOnly(plan.trainingStartDate) : null,
                      mono: true,
                    },
                    {
                      label: 'End date',
                      value: plan.trainingEndDate ? formatDateOnly(plan.trainingEndDate) : null,
                      mono: true,
                    },
                    // Why the employee last corrected it — an em dash while they never have.
                    { label: 'Remarks', value: plan.trainingRemarks ?? null },
                    {
                      label: 'Completed',
                      value: plan.trainingCompletedAt != null ? <Instant value={plan.trainingCompletedAt} /> : null,
                    },
                    { label: 'Completion remarks', value: plan.trainingCompletionRemarks ?? null },
                  ]}
                />
              </ProfileCard>
            ) : null}

            <ProfileCard id="development-plan-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: plan.createdByName ?? null },
                  {
                    label: 'Created',
                    value: <Instant value={plan.createdAt} />,
                  },
                  { label: 'Updated by', value: plan.updatedByName ?? null },
                  {
                    label: 'Updated',
                    value: plan.updatedAt != null ? <Instant value={plan.updatedAt} /> : null,
                  },
                ]}
              />
            </ProfileCard>
          </>
        )}
      </ProfileHeaderShell>

      {planning ? (
        <SurveyDevelopmentPlanModal
          open
          surveyId={plan.surveyId}
          respondentId={plan.id}
          respondentName={name ?? plan.employeeNumber}
          initialStep={planning}
          onClose={() => setPlanning(null)}
          onSaved={() => {
            setPlanning(null);
            load();
          }}
        />
      ) : null}

      {assessingImpact ? (
        <ImpactAssessmentModal
          detail={detail}
          employeeName={name ?? plan.employeeNumber}
          onClose={() => setAssessingImpact(false)}
          onSaved={() => {
            setAssessingImpact(false);
            load();
          }}
        />
      ) : null}

      {deciding ? (
        <DevelopmentPlanReviewModal
          open
          planId={plan.id}
          employeeName={name ?? plan.employeeNumber}
          initialStage={deciding}
          onClose={() => setDeciding(null)}
          onDecided={() => {
            setDeciding(null);
            load();
          }}
        />
      ) : null}

      {sending ? (
        <ConfirmDialog
          open
          id="development-plan-profile-send-dialog"
          title="Send development plan to employee"
          requireConfirm
          loading={running}
          onConfirm={() => void runSend()}
          onClose={() => {
            setSending(false);
            setSubmitError(null);
          }}
          body={
            <div className="flex flex-col gap-2 text-xs text-slate-600">
              <p>
                The employee can read the approved plan under Self Service as soon as it is sent, and is asked to accept
                or decline it. A plan is sent once and cannot be taken back.
              </p>
              {submitError ? <p className="text-red-700">{submitError.message}</p> : null}
            </div>
          }
        />
      ) : null}

      {submitting ? (
        <ConfirmDialog
          open
          id="development-plan-profile-submit-dialog"
          title="Submit development plan"
          requireConfirm
          loading={running}
          onConfirm={() => void runSubmit()}
          onClose={() => {
            setSubmitting(false);
            setSubmitError(null);
          }}
          body={
            <div className="flex flex-col gap-2 text-xs text-slate-600">
              <p>
                The development plan goes to its approver under Approvals. It cannot be changed while they hold it, and
                once approved it is final; a rejected plan can be revised and submitted again.
              </p>
              {submitError ? <p className="text-red-700">{submitError.message}</p> : null}
            </div>
          }
        />
      ) : null}
    </div>
  );
}

/**
 * Preview › Survey — the employee's answers, the ones the plan was written from, through Survey's
 * own read and its own `<ResponseAnswers>`, so one answer never reads two ways. Fetched when the
 * sub-tab OPENS; the tab is gated on `surveys.read`, and so is this read.
 */
function SurveyAnswersView({
  surveyId,
  respondentId,
  respondentName,
}: {
  surveyId: string;
  respondentId: string;
  respondentName: string;
}) {
  const [view, setView] = useState<SurveyResponseView | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    surveyRespondentsApi
      .get(surveyId, respondentId, controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setView(next);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        setError(caught instanceof ApiError ? caught : new ApiError("Could not load the employee's answers.", 0));
      });

    return () => controller.abort();
  }, [surveyId, respondentId, attempt]);

  if (error) {
    return (
      <ErrorState
        message={error.message}
        correlationId={error.correlationId}
        onRetry={() => {
          setError(null);
          setAttempt((a) => a + 1);
        }}
      />
    );
  }

  if (!view) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  return (
    // `<ResponseAnswers>` emits its blocks as SIBLINGS and leaves the gap to its container.
    <div className="flex flex-col gap-4">
      <ResponseAnswers view={view} respondentName={respondentName} />
    </div>
  );
}

function Instant({ value }: { value: string }) {
  return (
    <time dateTime={toIso(value)} className="font-mono">
      {formatDateTime(value)}
    </time>
  );
}

/** The header pill's tone — a profile may carry amber; an index page may not. */
function toneFor(plan: DevelopmentPlan): StatusTone {
  if (plan.developmentPlanAt == null) return 'inactive';

  switch (plan.developmentPlanStatus) {
    case SURVEY_PLAN_STATUS.Submitted:
      return 'pending';
    case SURVEY_PLAN_STATUS.Approved:
    case SURVEY_PLAN_STATUS.Accepted:
      return 'active';
    case SURVEY_PLAN_STATUS.Rejected:
    case SURVEY_PLAN_STATUS.Declined:
      return 'blocked';
    default:
      return 'inactive';
  }
}

/** The banner's sentence for every state — it renders for each, not only the bad ones. */
function bannerFor(
  plan: DevelopmentPlan,
  canDecide: boolean,
): {
  tone: ProfileBannerTone;
  icon: React.ReactNode;
  message: string;
} {
  const icon = (Glyph: typeof Clock) => <Glyph className="h-4 w-4" aria-hidden="true" />;

  if (!plan.isActive) {
    return {
      tone: 'red',
      icon: icon(Ban),
      message: 'This response is deleted, so its development plan can no longer be changed. Nothing is erased.',
    };
  }

  if (plan.developmentPlanAt == null) {
    return {
      tone: 'amber',
      icon: icon(FileText),
      message: `Assessed on ${formatDateTime(plan.assessedAt)}. A development plan has not been written yet.`,
    };
  }

  switch (plan.developmentPlanStatus) {
    case SURVEY_PLAN_STATUS.Submitted:
      return {
        tone: 'amber',
        icon: icon(Clock),
        // The approver it waits on is told so; everyone else is told where it is.
        message: canDecide
          ? 'This development plan is waiting for your approval. Read it under Preview, then approve or decline it.'
          : 'This development plan is waiting for approval under Approvals. It cannot be changed while the approver holds it.',
      };
    case SURVEY_PLAN_STATUS.Approved:
      return {
        tone: 'emerald',
        icon: icon(CheckCircle2),
        // Approved is not SENT, and sent is not the end — the employee accepts or declines it (12 Oct 2026).
        message:
          plan.sentAt == null
            ? 'This development plan has been approved. It has not been sent to the employee yet, so they cannot see it.'
            : `This development plan was approved and sent to the employee on ${formatDateTime(plan.sentAt)}. It is waiting for them to accept or decline it.`,
      };
    case SURVEY_PLAN_STATUS.Accepted:
      return {
        tone: 'emerald',
        icon: icon(CheckCircle2),
        message: `The employee accepted this development plan${plan.employeeDecidedAt != null ? ` on ${formatDateTime(plan.employeeDecidedAt)}` : ''}. It is final.`,
      };
    case SURVEY_PLAN_STATUS.Declined:
      return {
        tone: 'red',
        icon: icon(XCircle),
        // The reason is what the revision answers, so it is stated where the revising starts.
        message: `The employee declined this development plan${plan.employeeDecidedAt != null ? ` on ${formatDateTime(plan.employeeDecidedAt)}` : ''}: "${plan.declineReason ?? ''}". Revise it and submit it again.`,
      };
    case SURVEY_PLAN_STATUS.Rejected:
      return {
        tone: 'red',
        icon: icon(XCircle),
        message: 'This development plan was not approved. Revise it and submit it again.',
      };
    default:
      return {
        tone: 'slate',
        icon: icon(FileText),
        message: 'This development plan is written. It has not been submitted for approval yet.',
      };
  }
}
